import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import inviteService from '../../services/inviteService';
import alertify from 'alertifyjs';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';

/**
 * MyInvitesPage - User's pending invites management
 * 
 * Features:
 * - View all pending invites for current user
 * - Accept or reject invites
 * - Company information display
 * - Expiry date display
 */
const MyInvitesPage = () => {
  const history = useHistory();
  const hasPermission = useHasPermission('invites.my');
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const response = await inviteService.getUserPendingInvites();
      setInvites(response.data || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
      alertify.error('Davetler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (token) => {
    try {
      setActionLoading(token);
      await inviteService.acceptInvite(token);
      alertify.success('Davet kabul edildi! Şirkete katıldınız.');
      
      // Refresh invites
      await fetchInvites();
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        history.push('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Failed to accept invite:', error);
      alertify.error(error.response?.data?.message || 'Davet kabul edilemedi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (token) => {
    try {
      setActionLoading(token);
      await inviteService.rejectInvite(token);
      alertify.success('Davet reddedildi');
      
      // Refresh invites
      await fetchInvites();
    } catch (error) {
      console.error('Failed to reject invite:', error);
      alertify.error(error.response?.data?.message || 'Davet reddedilemedi');
    } finally {
      setActionLoading(null);
    }
  };

  const getRemainingDays = (expiresAt) => {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const handleBack = () => {
    history.push('/dashboard');
  };

  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Davetlerim
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Şirketlerden aldığınız davetleri görüntüleyin ve kabul edin
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Geri Dön
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Davetler yükleniyor...
          </Typography>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && invites.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Bekleyen Davet Yok
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Şu anda herhangi bir şirketten davet almadınız
          </Typography>
        </Paper>
      )}

      {/* Invites Table */}
      {!loading && invites.length > 0 && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium">
              Toplamda {invites.length} bekleyen davetiniz var.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Bir daveti kabul ettiğinizde, o şirkete otomatik olarak katılacaksınız.
            </Typography>
          </Alert>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Şirket</TableCell>
                  <TableCell>Davet Eden</TableCell>
                  <TableCell>Pozisyon</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Son Kullanma</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.map((invite) => {
                  const remainingDays = getRemainingDays(invite.expiresAt);
                  const isExpiringSoon = remainingDays <= 2;

                  return (
                    <TableRow key={invite._id} hover>
                      {/* Company */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {invite.companyId?.companyName || 'Bilinmiyor'}
                        </Typography>
                      </TableCell>

                      {/* Invited By */}
                      <TableCell>
                        <Typography variant="body2">
                          {invite.invitedBy?.firstName} {invite.invitedBy?.lastName}
                        </Typography>
                      </TableCell>

                      {/* Position */}
                      <TableCell>
                        <Chip
                          label={invite.userType === 'company_member' ? 'Şirket Üyesi' : invite.userType}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(invite.createdAt), 'dd MMM yyyy', { locale: tr })}
                        </Typography>
                      </TableCell>

                      {/* Expiry */}
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          color={isExpiringSoon ? 'error' : 'text.primary'}
                          fontWeight={isExpiringSoon ? 'medium' : 'normal'}
                        >
                          {remainingDays} gün kaldı
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleAccept(invite.token)}
                            disabled={actionLoading === invite.token}
                          >
                            {actionLoading === invite.token ? 'İşleniyor...' : 'Kabul Et'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={() => handleReject(invite.token)}
                            disabled={actionLoading === invite.token}
                          >
                            Reddet
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      </Box>
  );
};

export default MyInvitesPage;

